import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog'
import { Button } from '../ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { Separator } from '../ui/separator'
import { Loader2, Crown, Sparkles, Download, CheckCircle, XCircle, ExternalLink, AlertCircle, Gift, Users } from 'lucide-react'
import { apiClient } from '../../lib/api'
import type { MembershipTierConfig, MembershipTier, UserReward } from '../../types'
import { MembershipTierCard } from './MembershipTierCard'

interface DownloadLimitDialogProps {
  open: boolean
  onClose: () => void
  onUpgraded: () => void
  currentTier: MembershipTier
  downloadsUsed: number
  downloadsLimit: number
}

interface UpgradeResponse {
  success: boolean
  membership?: any
  subscription_id?: string
  checkout_url?: string
  session_id?: string
  status?: string
  message?: string
  error?: string
  requires_payment?: boolean
  tier?: string
}

type PaymentStatus = 'idle' | 'selecting' | 'waiting' | 'completed' | 'failed';

const POLL_INTERVAL = 2000; // 2 seconds
const MAX_POLL_TIME = 10 * 60 * 1000; // 10 minutes max wait

export function DownloadLimitDialog({ 
  open, 
  onClose, 
  onUpgraded, 
  currentTier: _currentTier,
  downloadsUsed,
  downloadsLimit 
}: DownloadLimitDialogProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [tiers, setTiers] = useState<MembershipTierConfig[]>([])
  const [loading, setLoading] = useState(true)
  const [upgrading, setUpgrading] = useState(false)
  const [selectedTier, setSelectedTier] = useState<MembershipTierConfig | null>(null)
  
  // Payment state
  const [paymentStatus, setPaymentStatus] = useState<PaymentStatus>('idle')
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null)
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const pollStartTimeRef = useRef<number>(0)
  const checkoutWindowRef = useRef<Window | null>(null)

  // Download rewards state
  const [downloadRewards, setDownloadRewards] = useState<UserReward[]>([])
  const [loadingRewards, setLoadingRewards] = useState(false)
  const [claimingReward, setClaimingReward] = useState<number | null>(null)

  useEffect(() => {
    if (open) {
      loadTiers()
      loadDownloadRewards()
    }
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current)
      }
    }
  }, [open])

  const loadTiers = async () => {
    try {
      setLoading(true)
      const response = await apiClient.membership.getMembershipTiers()
      if (response.success) {
        // Filter to only show tiers with more downloads than current
        const upgradeTiers = response.tiers.filter(
          (tier: MembershipTierConfig) => tier.free_downloads_per_month > downloadsLimit
        )
        setTiers(upgradeTiers)
      }
    } catch (error) {
      console.error('Error loading tiers:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadDownloadRewards = async () => {
    try {
      setLoadingRewards(true)
      const response = await apiClient.rewards.getPendingRewards()
      // Filter only bonus_downloads type rewards
      const downloads = response.rewards.filter(
        (r: UserReward) => r.reward?.reward_type === 'bonus_downloads' && (r.reward?.bonus_download_amount ?? 0) > 0
      )
      setDownloadRewards(downloads)
    } catch (error) {
      console.error('Error loading download rewards:', error)
    } finally {
      setLoadingRewards(false)
    }
  }

  const handleClaimReward = async (userRewardId: number) => {
    try {
      setClaimingReward(userRewardId)
      const result = await apiClient.rewards.claimReward(userRewardId)
      
      if (result.success) {
        // Remove claimed reward from list
        setDownloadRewards(prev => prev.filter(r => r.id !== userRewardId))
        
        // If bonus downloads were added, close dialog and notify parent
        if (result.bonus_download_amount && result.bonus_download_amount > 0) {
          onUpgraded()
          onClose()
        }
      }
    } catch (error) {
      console.error('Error claiming reward:', error)
    } finally {
      setClaimingReward(null)
    }
  }

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
  }

  const startPolling = useCallback((tier: string) => {
    pollStartTimeRef.current = Date.now()
    
    pollIntervalRef.current = setInterval(async () => {
      // Check if we've been polling too long
      if (Date.now() - pollStartTimeRef.current > MAX_POLL_TIME) {
        stopPolling()
        setPaymentStatus('failed')
        return
      }
      
      try {
        const statusResult = await apiClient.membership.getUpgradeStatus(tier)
        
        if (statusResult.status === 'completed') {
          stopPolling()
          setPaymentStatus('completed')
          
          // Close checkout tab if still open
          if (checkoutWindowRef.current && !checkoutWindowRef.current.closed) {
            checkoutWindowRef.current.close()
          }
        } else if (statusResult.status === 'failed') {
          stopPolling()
          setPaymentStatus('failed')
        }
        // If still 'pending', continue polling
      } catch (error) {
        console.error('Error checking upgrade status:', error)
        // Continue polling on error
      }
    }, POLL_INTERVAL)
  }, [])

  const handleUpgrade = async (tier: MembershipTier) => {
    try {
      setUpgrading(true)
      const tierConfig = tiers.find(t => t.tier_id === tier)
      setSelectedTier(tierConfig || null)
      
      const response = await apiClient.membership.upgradeMembership(tier) as UpgradeResponse
      
      if (response.success) {
        // Check if payment is required (checkout_url returned)
        if (response.checkout_url) {
          // Open Stripe Checkout in new tab
          setCheckoutUrl(response.checkout_url)
          checkoutWindowRef.current = window.open(response.checkout_url, '_blank')
          setPaymentStatus('waiting')
          
          // Start polling for upgrade completion
          startPolling(tier)
          
          setUpgrading(false)
          return
        }
        
        // No payment required (free tier or already paid)
        onUpgraded()
        onClose()
      } else {
        alert(response.error || response.message || 'Failed to upgrade membership')
      }
    } catch (error: any) {
      console.error('Error upgrading membership:', error)
      alert(error.message || 'Failed to upgrade membership')
    } finally {
      setUpgrading(false)
    }
  }

  const handleClose = () => {
    stopPolling()
    
    if (paymentStatus === 'completed') {
      onUpgraded()
    }
    
    // Close checkout tab if still open
    if (checkoutWindowRef.current && !checkoutWindowRef.current.closed) {
      checkoutWindowRef.current.close()
    }
    
    // Reset state
    setPaymentStatus('idle')
    setCheckoutUrl(null)
    setSelectedTier(null)
    
    onClose()
  }

  const handleOpenCheckout = () => {
    if (checkoutUrl) {
      checkoutWindowRef.current = window.open(checkoutUrl, '_blank')
    }
  }

  const handleCancel = () => {
    stopPolling()
    setPaymentStatus('idle')
    setCheckoutUrl(null)
    setSelectedTier(null)
    
    // Close checkout tab if still open
    if (checkoutWindowRef.current && !checkoutWindowRef.current.closed) {
      checkoutWindowRef.current.close()
    }
  }

  const getTierIcon = (tierId: string) => {
    switch (tierId) {
      case 'book_giant':
        return <Crown className="h-5 w-5 text-yellow-500" />
      case 'plus':
        return <Sparkles className="h-5 w-5 text-purple-500" />
      default:
        return <Download className="h-5 w-5 text-blue-500" />
    }
  }

  // Show payment waiting/completion dialog
  if (paymentStatus === 'waiting' || paymentStatus === 'completed' || paymentStatus === 'failed') {
    return (
      <Dialog open={open} onOpenChange={() => paymentStatus !== 'waiting' && handleClose()}>
        <DialogContent className="download-limit-dialog sm:max-w-md max-h-[calc(100dvh-2rem)] flex flex-col p-4 sm:p-6" onPointerDownOutside={(e) => paymentStatus === 'waiting' && e.preventDefault()}>
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              {paymentStatus === 'completed' ? (
                <CheckCircle className="h-5 w-5 text-green-600" />
              ) : paymentStatus === 'failed' ? (
                <XCircle className="h-5 w-5 text-destructive" />
              ) : (
                <Loader2 className="h-5 w-5 animate-spin" />
              )}
              {paymentStatus === 'completed' 
                ? t('payment.membershipActivated', 'Membership Activated!')
                : paymentStatus === 'failed'
                ? t('payment.paymentFailed', 'Payment Failed')
                : t('membership.upgrading', 'Upgrading Membership')
              }
            </DialogTitle>
            <DialogDescription>
              {paymentStatus === 'waiting' 
                ? t('payment.completeInNewTab', 'Complete your payment in the new tab. This dialog will update automatically.')
                : paymentStatus === 'completed'
                ? t('membership.takeLimitIncreased', 'Your take limit has been increased. You can now take more books!')
                : t('payment.paymentDidNotComplete', 'The payment did not complete. Please try again.')
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-3 sm:py-4 flex-1 overflow-y-auto min-h-0">
            {/* Selected Tier Info */}
            {selectedTier && paymentStatus !== 'completed' && (
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  {getTierIcon(selectedTier.tier_id)}
                  <div>
                    <p className="font-semibold">{selectedTier.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {selectedTier.free_downloads_per_month} {t('membership.takesPerMonth', 'takes/month')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Status Display */}
            {paymentStatus === 'waiting' && (
              <div className="flex flex-col items-center justify-center py-6 space-y-4">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground text-center">
                  {t('payment.waitingForPayment', 'Waiting for payment confirmation...')}
                </p>
              </div>
            )}

            {paymentStatus === 'completed' && (
              <div className="flex flex-col items-center justify-center py-6 space-y-4">
                <div className="w-14 h-14 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <div className="text-center">
                  <p className="text-lg font-semibold">
                    {selectedTier?.free_downloads_per_month} {t('membership.takesNowAvailable', 'takes now available!')}
                  </p>
                </div>
              </div>
            )}

            {paymentStatus === 'failed' && (
              <div className="flex flex-col items-center justify-center py-6 space-y-4">
                <div className="w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center">
                  <XCircle className="h-8 w-8 text-destructive" />
                </div>
                <p className="text-sm text-muted-foreground text-center">
                  {t('payment.tryAgainOrContact', 'Please try again or contact support if the problem persists.')}
                </p>
              </div>
            )}

          </div>

          {/* Action Buttons - fixed at bottom */}
          <div className="flex-shrink-0 pt-2 space-y-2">
            {paymentStatus === 'waiting' && (
              <>
                <Button
                  onClick={handleOpenCheckout}
                  variant="outline"
                  className="w-full"
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  {t('payment.reopenPaymentPage', 'Reopen Payment Page')}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleCancel}
                  className="w-full"
                >
                  {t('common.cancel', 'Cancel')}
                </Button>
              </>
            )}
            
            {paymentStatus === 'completed' && (
              <Button onClick={handleClose} className="w-full">
                {t('common.continue', 'Continue')}
              </Button>
            )}
            
            {paymentStatus === 'failed' && (
              <>
                <Button onClick={handleOpenCheckout} className="w-full">
                  {t('payment.tryAgain', 'Try Again')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancel}
                  className="w-full"
                >
                  {t('common.cancel', 'Cancel')}
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  // Show limit reached + upgrade options dialog
  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="download-limit-dialog sm:max-w-lg max-h-[calc(100dvh-2rem)] flex flex-col p-4 sm:p-6">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-amber-500" />
            {t('membership.takeLimitReached', 'Take Limit Reached')}
          </DialogTitle>
          <DialogDescription>
            {t('membership.takeLimitDesc', "You've used all your free book takes for this month.")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 sm:space-y-4 py-2 flex-1 overflow-y-auto min-h-0">
          {/* Usage Display */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {t('membership.takesUsed', 'Takes used')}
              </span>
              <span className="font-semibold text-foreground">
                {downloadsUsed} / {downloadsLimit}
              </span>
            </div>
            <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-amber-500 rounded-full" 
                style={{ width: '100%' }}
              />
            </div>
          </div>

          {/* Get Free Downloads Section */}
          <Card className="free-takes-block border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-950/20">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                <Sparkles className="h-4 w-4" />
                {t('membership.getFreeTakes', 'Get Free Book Takes')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Unclaimed download rewards */}
              {loadingRewards ? (
                <div className="loading-rewards flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {t('rewards.checkingRewards', 'Checking for rewards...')}
                </div>
              ) : downloadRewards.length > 0 ? (
                <div className="unclaimed-rewards space-y-2">
                  <p className="text-xs text-muted-foreground">
                    {t('rewards.claimPending', 'Claim your pending rewards:')}
                  </p>
                  {downloadRewards.map(reward => (
                    <div 
                      key={reward.id}
                      className="reward-item flex items-center justify-between p-2 rounded-md bg-white dark:bg-gray-900 border border-emerald-200 dark:border-emerald-800"
                    >
                      <div className="flex items-center gap-2">
                        <div className="icon-wrapper p-1 rounded bg-emerald-100 dark:bg-emerald-900">
                          <Gift className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                        </div>
                        <div>
                          <p className="text-xs font-medium">{reward.reward?.name}</p>
                          <p className="text-sm font-bold text-emerald-600 dark:text-emerald-400">
                            +{reward.reward?.bonus_download_amount} {t('membership.takes', 'takes')}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-700 dark:text-emerald-400 dark:hover:bg-emerald-900"
                        onClick={() => handleClaimReward(reward.id)}
                        disabled={claimingReward !== null}
                      >
                        {claimingReward === reward.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          t('rewards.claim', 'Claim')
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              ) : null}

              {/* Invite friends option */}
              <div className="invite-friends-option">
                {downloadRewards.length > 0 && (
                  <Separator className="my-2" />
                )}
                <div className="flex items-center gap-3 p-2 rounded-md bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-950/30 dark:to-pink-950/30 border border-purple-200 dark:border-purple-800">
                  <div className="icon-wrapper p-1.5 rounded-full bg-purple-100 dark:bg-purple-900">
                    <Users className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-medium text-purple-700 dark:text-purple-300">
                      {t('referrals.inviteFriends', 'Invite friends')}
                    </p>
                    <p className="text-[10px] text-purple-600/70 dark:text-purple-400/70">
                      {t('referrals.bothEarnTakes', 'Both earn bonus takes')}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs text-purple-700 hover:text-purple-800 hover:bg-purple-100 dark:text-purple-400 dark:hover:bg-purple-900"
                    onClick={() => {
                      onClose()
                      navigate('/referrals')
                    }}
                  >
                    {t('referrals.share', 'Share')} →
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Upgrade Options */}
          {loading ? (
            <div className="flex items-center justify-center p-6">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : tiers.length > 0 ? (
            <div className="space-y-3">
              <p className="text-sm font-medium text-foreground">
                {t('membership.upgradeForMore', 'Upgrade to take more books:')}
              </p>
              
              {tiers.map((tier) => (
                <MembershipTierCard
                  key={tier.tier_id}
                  tier={tier}
                  canUpgrade={!upgrading}
                  upgrading={upgrading}
                  onUpgrade={handleUpgrade}
                  variant="compact"
                />
              ))}
            </div>
          ) : (
            <div className="text-center p-4 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground">
                {t('membership.alreadyMaxTier', "You're already on the highest tier. Takes will reset at the start of your next billing cycle.")}
              </p>
            </div>
          )}

        </div>

        {/* Close button - fixed at bottom */}
        <div className="flex-shrink-0 pt-2">
          <Button
            variant="ghost"
            onClick={handleClose}
            className="w-full"
          >
            {t('common.maybeLater', 'Maybe Later')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

